import React, { useEffect } from 'react';
import {
  CylinderGeometry,
  Euler,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three';

import { PlatePair, Point2D, Point3D } from '@paragon/contract-library';
import { convertDegreesToRadians } from '@paragon/geometry-library';
import { RenderOrder } from '@paragon/three-shared';

import { useAppSelector } from '../../components/hooks';

// TODO for performance use points and lines vs geometry equivalent
const lineGeometry = new CylinderGeometry(0.02, 0.02, 1, 8, 1, false);
const pointGeometry = new SphereGeometry(0.03, 8, 8);

const outlineMaterial = new MeshBasicMaterial({ transparent: true, color: 0x4682b4, depthTest: false });
const shearLineMaterial = new MeshBasicMaterial({ transparent: true, color: 0x006400, depthTest: false });
const polygonLineMaterial = new MeshBasicMaterial({ transparent: true, color: 0xcc00cc, depthTest: false });
const endPointMaterial = new MeshBasicMaterial({ transparent: true, color: 0x00ffff, depthTest: false });

interface ShearLineData {
  start: Point2D;
  end: Point2D;
  openAreas: readonly Point2D[][];
}

interface PlateShearLineVisualProps {
  readonly guid: string;
  width: number;
  length: number;
  position: Point3D;
  distanceBetweenPlates: number;
  angle: number;
  mergedShearLines: readonly ShearLineData[];
  unmergedShearLines: readonly ShearLineData[];
}

class PlateShearLineVisualObject extends Object3D {
  public readonly guid: string;
  readonly props: PlateShearLineVisualProps;
  private isMerged = false;
  private isVisible = false;
  private openAreaLines: Mesh[] = [];
  private shearLines: Mesh[] = [];
  private outlineContainer: Object3D | null = null;
  private points: Object3D[] = [];
  constructor(props: PlateShearLineVisualProps) {
    super();
    this.guid = props.guid;
    this.props = props;
  }

  public setMergedOrUnmerged(isMerged: boolean) {
    let rebuild = false;
    if (isMerged && !this.isMerged && this.isVisible) {
      rebuild = true;
    }
    if (!isMerged && this.isMerged && this.isVisible) {
      rebuild = true;
    }
    this.isMerged = isMerged;
    if (rebuild) {
      this.cleanupOpenAreaLines();
      this.cleanupShearLines();
      this.cleanupPoints();
      this.setupShearLines();
      this.setupOpenAreas();
    }
  }

  public setVisible(isVisible: boolean) {
    if (isVisible && !this.isVisible) {
      this.setupShearLines();
      this.setupOpenAreas();
      this.setupOutline();
    } else if (!isVisible && this.isVisible) {
      this.cleanupOpenAreaLines();
      this.cleanupOutline();
      this.cleanupShearLines();
      this.cleanupPoints();
    }
    this.isVisible = isVisible;
  }

  private getPlateMatrix() {
    const translation = new Matrix4().makeTranslation(
      this.props.position.x,
      this.props.position.y,
      this.props.position.z + this.props.distanceBetweenPlates / 2,
    );
    const euler = new Euler(0, 0, this.props.angle);
    const rotation = new Matrix4().makeRotationFromEuler(euler);
    return translation.multiply(rotation);
  }

  private setupOutline() {
    const { length, width } = this.props;

    const container = new Object3D();
    container.applyMatrix4(this.getPlateMatrix());

    const right = new Mesh(lineGeometry, outlineMaterial);
    right.scale.y = width;
    right.position.x = length / 2;
    right.renderOrder = RenderOrder.PlateShearLineVisual_Outline;
    container.add(right);

    const left = new Mesh(lineGeometry, outlineMaterial);
    left.scale.y = width;
    left.position.x = -(length / 2);
    left.renderOrder = RenderOrder.PlateShearLineVisual_Outline;
    container.add(left);

    const top = new Mesh(lineGeometry, outlineMaterial);
    top.scale.y = length;
    top.position.y = width / 2;
    top.quaternion.setFromAxisAngle(new Vector3(0, 0, 1), Math.PI / 2);
    top.renderOrder = RenderOrder.PlateShearLineVisual_Outline;
    container.add(top);

    const bottom = new Mesh(lineGeometry, outlineMaterial);
    bottom.scale.y = length;
    bottom.position.y = -(width / 2);
    bottom.quaternion.setFromAxisAngle(new Vector3(0, 0, 1), Math.PI / 2);
    bottom.renderOrder = RenderOrder.PlateShearLineVisual_Outline;
    container.add(bottom);

    this.outlineContainer = container;
    this.add(container);
  }

  private setupShearLines() {
    const shearLines = this.isMerged ? this.props.mergedShearLines : this.props.unmergedShearLines;
    shearLines.forEach((shearLine) => {
      const start = new Vector3(shearLine.start.x, shearLine.start.y, 0);
      const end = new Vector3(shearLine.end.x, shearLine.end.y, 0);
      const difference = new Vector3().copy(end).sub(start);
      const midPoint = new Vector3().lerpVectors(start, end, 0.5);
      const direction = new Vector3().copy(difference).normalize();
      const quaternion = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), direction);
      const shearLineMesh = new Mesh(lineGeometry, shearLineMaterial);
      shearLineMesh.quaternion.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
      shearLineMesh.position.set(midPoint.x, midPoint.y, midPoint.z);
      shearLineMesh.renderOrder = RenderOrder.PlateShearLineVisual_ShearLine;
      shearLineMesh.scale.y = difference.length();
      this.shearLines.push(shearLineMesh);
      this.add(shearLineMesh);
      this.buildPoint(start);
      this.buildPoint(end);
    });
  }

  private setupOpenAreas() {
    const shearLines = this.isMerged ? this.props.mergedShearLines : this.props.unmergedShearLines;
    shearLines.forEach((shearLine) => {
      const { openAreas } = shearLine;
      openAreas.forEach((polygon) => {
        let previousPoint: Point2D | undefined;
        polygon.forEach((point) => {
          this.buildPoint(new Vector3(point.x, point.y, 0));
          if (!previousPoint) {
            previousPoint = polygon[polygon.length - 1];
          }
          const start = new Vector3(previousPoint.x, previousPoint.y, 0);
          const end = new Vector3(point.x, point.y, 0);
          const difference = new Vector3().copy(end).sub(start);
          const midPoint = new Vector3().lerpVectors(start, end, 0.5);
          const direction = new Vector3().copy(difference).normalize();
          const quaternion = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), direction);
          const line = new Mesh(lineGeometry, polygonLineMaterial);
          line.quaternion.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
          line.position.set(midPoint.x, midPoint.y, midPoint.z);
          line.renderOrder = RenderOrder.PlateShearLineVisual_ShearLine;
          line.scale.y = difference.length();
          this.openAreaLines.push(line);
          this.add(line);
          previousPoint = point;
        });
      });
    });
  }

  private buildPoint(position: Vector3) {
    const point = new Mesh(pointGeometry, endPointMaterial);
    point.renderOrder = RenderOrder.PlateShearLineVisual_Point;
    point.position.set(position.x, position.y, position.z);
    this.points.push(point);
    this.add(point);
  }

  private cleanupOutline() {
    if (this.outlineContainer) {
      this.remove(this.outlineContainer);
    }
    this.outlineContainer = null;
  }

  private cleanupShearLines() {
    this.shearLines.forEach((shearLine) => {
      this.remove(shearLine);
    });
    this.shearLines = [];
  }

  private cleanupOpenAreaLines() {
    this.openAreaLines.forEach((openAreaLine) => {
      this.remove(openAreaLine);
    });
    this.openAreaLines = [];
  }
  private cleanupPoints() {
    this.points.forEach((point) => {
      this.remove(point);
    });
    this.points = [];
  }

  public dispose() {
    this.cleanupOpenAreaLines();
    this.cleanupShearLines();
    this.cleanupOutline();
    this.cleanupPoints();
  }
}

export const PlateShearLineVisual = (props: { platePair: PlatePair }) => {
  const { platePair } = props;
  const shearLineVisualProps = {
    guid: platePair.guid,
    width: platePair.width,
    length: platePair.length,
    position: {
      x: platePair.centerX,
      y: platePair.centerY,
      z: platePair.centerZ,
    },
    angle: convertDegreesToRadians(platePair.angleZ),
    distanceBetweenPlates: platePair.distanceBetweenPlates,
    mergedShearLines: [...platePair.mergedShearLines],
    unmergedShearLines: [...platePair.unmergedShearLines],
  };

  const shearLineViewMode = useAppSelector((state) => state.visualOptions.shearLineViewMode);

  const object = new PlateShearLineVisualObject(shearLineVisualProps);
  object.setVisible(shearLineViewMode !== 'NONE');
  object.setMergedOrUnmerged(shearLineViewMode === 'MERGED');
  useEffect(() => () => object.dispose());

  if (platePair.geometryType !== 'Rectangular') return null;

  return <primitive object={object} />;
};
